import { Injectable, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../api/services/api.service';
import { Language } from '../models/language.model';
import { LanguageResponse } from '../interfaces/language.response';
import { LanguageFactory } from '../factories/language.factory';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { HeaderService } from '../../api/services/header.service';

@Injectable()
export class LanguageService {
  /**
   * This event fires, when the current language is changed.
   *
   * @type {EventEmitter<string>}
   * @memberof LanguageService
   */
  public onLangChanged: EventEmitter<string> = new EventEmitter<string>();

  /**
   * This event fires, when the list of the available languages changed.
   *
   * @type {EventEmitter<void>}
   * @memberof LanguageService
   */
  public onLanguagesListChanged: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private readonly translateService: TranslateService,
    // private readonly userService: JwtAuthenticationService, with HTTP
    private readonly apiService: ApiService,
    private readonly headerService: HeaderService
  ) {
    /* with HTTP
    this.userService.currentUser.subscribe(() => {
      this.getLanguages().subscribe(res => this.handleLanguagesResponse(res), err => this.handleErrors(err));
    });*/
  }

  /**
   * Factory of the service.
   *
   * @private
   * @memberof LanguageService
   */
  private readonly factory = new LanguageFactory();

  /**
   * Default/Offline language.
   *
   * @private
   * @type {string}
   * @memberof LanguageService
   */
  private readonly storedDefaultLocale: string = 'hu';

  /**
   * Default language code.
   *
   * @private
   * @type {string[]}
   * @memberof LanguageService
   */
  private readonly storedDefaultAvailableLanguages: string[] = ['en', 'hu'];

  /**
   * Default language code.
   *
   * @private
   * @type {string}
   * @memberof LanguageService
   */
  private storedDefaultLang: string;

  /**
   * Current language code.
   *
   * @private
   * @type {string}
   * @memberof LanguageService
   */
  private storedCurrentLang: string;

  /**
   * Name of the cookie.
   *
   * @private
   * @memberof LanguageService
   */
  private readonly languageCookieName = 'language';

  /**
   * List of the available languages. (Object array)
   *
   * @private
   * @type {Language[]}
   * @memberof LanguageService
   */
  private storedAvailableLanguages: Language[] = [];

  /**
   * Gets the current Core language.
   *
   * @readonly
   * @type {string}
   * @memberof LanguageService
   */
  public get currentLang(): string {
    return this.storedCurrentLang || this.storedDefaultLang;
  }

  /**
   * Gets the default Core languge code.
   *
   * @readonly
   * @type {string}
   * @memberof LanguageService
   */
  public get defaultLang(): string {
    return this.storedDefaultLang;
  }

  /**
   * Gets the current available languages.
   *
   * @readonly
   * @type {Language[]}
   * @memberof LanguageService
   */
  public get availableLanguages(): Language[] {
    return this.storedAvailableLanguages;
  }

  /**
   * Gets the current available language codes.
   *
   * @readonly
   * @memberof LanguageService
   */
  public get availableKeys() {
    const langs = this.translateService.getLangs().filter((lang) => lang !== this.currentLang);
    langs.unshift(this.currentLang);
    return langs;
  }

  /**
   * Initialize the service.
   *
   * @memberof LanguageService
   */
  public initialize() {
    this.getLanguages().subscribe((res) => this.handleLanguagesResponse(res), (err) => this.handleErrors(err));
  }

  /**
   * Initialize the service with the json.
   *
   * @memberof LanguageService
   */
  public initializeOffline(lang: string) {
    this.setLanguage(lang);
  }

  /**
   * Sets the default language.
   *
   * @param {string} lang
   * @memberof LanguageService
   */
  public setDefaultLang(lang: string) {
    this.storedDefaultLang = lang;
    this.translateService.setDefaultLang(lang);
  }

  /**
   * Sets the language.
   *
   * @param {string} lang
   * @param {boolean} [doStore=false] True, if you want to store the language in the cookie.
   * @memberof LanguageService
   */
  public setLanguage(lang: string, doStore: boolean = false): void {
    if (doStore) {
      this.setLanguageToCookie(lang);
    }

    // Core
    this.storedCurrentLang = lang;

    // moment
    moment.locale(lang);

    // ngx-translate
    this.translateService.use(lang);

    // Core - Header service
    this.headerService.setLocale(lang);

    // Gloobal signal
    this.onLangChanged.emit();
  }

  /**
   * Error handler.
   *
   * @private
   * @param {*} err
   * @memberof LanguageService
   */
  private handleErrors(err) {
    this.setDefaults();
    console.error(err);
  }

  /**
   * Api response handler.
   *
   * @private
   * @param {LanguageResponse[]} res
   * @memberof LanguageService
   */
  private handleLanguagesResponse(res: LanguageResponse[]) {
    const actives = res.filter((i) => i.active);

    // this language will be used as a fallback when a translation isn't found in the current language
    this.setDefaultLang(actives.filter((i) => i.default)[0].locale);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this.setLanguage(this.getLanguageFromCookie());

    // Language changed
    this.onLangChanged.emit(this.getLanguageFromCookie());

    // List must be filled
    this.storedAvailableLanguages = [];
    actives.forEach((i) => this.storedAvailableLanguages.push(this.factory.loadFromResponse(i)));
    this.translateService.addLangs(this.storedAvailableLanguages.map((i) => i.locale));
    this.onLanguagesListChanged.emit();
  }

  /**
   * Gets the languages from the Backend.
   *
   * @private
   * @returns {Observable<LanguageResponse[]>}
   * @memberof LanguageService
   */
  private getLanguages(): Observable<LanguageResponse[]> {
    return this.apiService.getOne<LanguageResponse[]>('/language');
  }

  private setDefaults() {
    this.translateService.addLangs(this.storedDefaultAvailableLanguages);
    this.setLanguage(this.getLanguageFromCookie());
  }

  /**
   * Gets the language from the cookie.
   *
   * @private
   * @returns
   * @memberof LanguageService
   */
  private getLanguageFromCookie() {
    const value = window.localStorage[this.languageCookieName];
    return value ? value : this.storedDefaultLocale;
  }

  /**
   * Sets the cookie for the language.
   *
   * @private
   * @param {string} value
   * @memberof LanguageService
   */
  private setLanguageToCookie(value: string) {
    window.localStorage[this.languageCookieName] = value;
  }
}
